/**
 * ULD历史记录主页面
 * 整合筛选器和表格，提供完整的历史记录查看功能
 */

import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Card, Space, Typography, message, Alert, Button } from "antd";
import { HistoryOutlined, DownloadOutlined } from "@ant-design/icons";

import { uldService } from "../../../../api/enhancedApi";
import { handleExportDownload } from "../../../../utils/exportUtils";
import UldHistoryFilter from "./UldHistoryFilter";
import UldHistoryTable from "./UldHistoryTable";
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE } from "../constants/historyConstants";

const { Title } = Typography;

const UldHistoryPage = () => {
    const location = useLocation();

    // 状态管理
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [pagination, setPagination] = useState({
        current: DEFAULT_PAGE,
        pageSize: DEFAULT_PAGE_SIZE,
        total: 0,
    });
    const [filters, setFilters] = useState({});
    const [initialFiltersSet, setInitialFiltersSet] = useState(false);

    // 解析URL查询参数
    const parseUrlParams = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const params = {};

        // 获取ULD识别码
        const uldIdentifier = searchParams.get("uld_identifier");
        if (uldIdentifier) {
            params.uld_identifier = uldIdentifier;
        }

        // 检查是否自动搜索
        const autoSearch = searchParams.get("auto_search");

        return { params, autoSearch: autoSearch === "true" };
    }, [location.search]);

    // 初始化URL参数
    useEffect(() => {
        const { params, autoSearch } = parseUrlParams();

        if (Object.keys(params).length > 0) {
            console.log("ULD历史页面: 检测到URL参数，ULD识别码:", params.uld_identifier);
            setFilters(params);
            setInitialFiltersSet(true);

            // 如果标记了自动搜索，则立即执行搜索
            if (autoSearch) {
                console.log("ULD历史页面: 自动执行搜索");
                // 稍微延迟一下，确保状态更新完成
                setTimeout(() => {
                    fetchHistoryData(params);
                }, 100);
            }
        } else {
            setInitialFiltersSet(true);
        }
    }, [location.search]);

    // 初始化加载（仅在没有URL参数时执行）
    useEffect(() => {
        if (initialFiltersSet && Object.keys(filters).length === 0) {
            fetchHistoryData();
        }
    }, [initialFiltersSet]);

    // 获取历史记录数据
    const fetchHistoryData = useCallback(
        async (params = {}) => {
            setLoading(true);
            try {
                const requestParams = {
                    page: pagination.current,
                    page_size: pagination.pageSize,
                    ...filters,
                    ...params,
                };

                console.log("请求历史记录参数:", requestParams);

                const response = await uldService.listUldHistory(requestParams);
                const { items, total } = response.data;

                setHistoryData(items || []);
                setPagination((prev) => ({
                    ...prev,
                    total,
                }));
            } catch (error) {
                console.error("获取历史记录失败:", error);
                message.error(error.response?.data?.detail || "获取历史记录失败");
                setHistoryData([]);
            } finally {
                setLoading(false);
            }
        },
        [pagination.current, pagination.pageSize, filters],
    );

    // 处理筛选
    const handleFilter = async (newFilters) => {
        console.log("应用筛选条件:", newFilters);
        setFilters(newFilters);
        setPagination((prev) => ({ ...prev, current: 1 }));

        // 直接调用fetchData，使用新的筛选条件
        setLoading(true);
        try {
            const params = {
                page: 1,
                page_size: pagination.pageSize,
                ...newFilters,
            };

            const response = await uldService.listUldHistory(params);

            setHistoryData(response.data.items || []);
            setPagination((prev) => ({
                ...prev,
                current: 1,
                total: response.data.total,
            }));

            // 如果使用了多个ULD识别码，显示成功提示
            if (newFilters.uld_identifiers) {
                const identifierCount = newFilters.uld_identifiers.split(",").length;
                message.success(`批量查询完成，共找到 ${response.data.total} 条历史记录`);
            }
        } catch (error) {
            console.error("获取历史记录失败:", error);
            message.error(error.response?.data?.detail || "获取历史记录失败");
            setHistoryData([]);
        } finally {
            setLoading(false);
        }
    };

    // 处理重置
    const handleReset = () => {
        setFilters({});
        setPagination((prev) => ({ ...prev, current: 1 }));
        fetchHistoryData({});
    };

    // 处理分页变化
    const handlePageChange = (page, pageSize) => {
        setPagination((prev) => ({
            ...prev,
            current: page,
            pageSize: pageSize || prev.pageSize,
        }));

        // 检查是否是批量查询结果的分页
        if (window._batchQueryResults) {
            // 使用前端分页
            const total = window._batchQueryResults.length;
            const currentPageSize = pageSize || pagination.pageSize;
            const startIndex = (page - 1) * currentPageSize;
            const endIndex = Math.min(startIndex + currentPageSize, total);
            const pagedItems = window._batchQueryResults.slice(startIndex, endIndex);

            setHistoryData(pagedItems);
            setPagination((prev) => ({
                ...prev,
                current: page,
                pageSize: currentPageSize,
                total,
            }));
            return;
        }

        // 正常的后端分页
        const newParams = {
            page,
            page_size: pageSize || pagination.pageSize,
            ...filters,
        };

        setLoading(true);
        uldService
            .listUldHistory(newParams)
            .then((response) => {
                const { items, total } = response.data;
                setHistoryData(items || []);
                setPagination((prev) => ({
                    ...prev,
                    current: page,
                    pageSize: pageSize || prev.pageSize,
                    total,
                }));
            })
            .catch((error) => {
                console.error("获取历史记录失败:", error);
                message.error(error.response?.data?.detail || "获取历史记录失败");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // 处理页面大小变化
    const handleShowSizeChange = (current, size) => {
        handlePageChange(current, size);
    };

    // 处理导出
    const handleExport = async () => {
        if (pagination.total === 0) {
            message.warning("当前没有可导出的历史记录数据");
            return;
        }

        setExportLoading(true);
        try {
            const exportData = {
                filters: filters,
                options: {
                    include_all: true, // 导出所有筛选结果，不限制分页
                },
            };

            console.log("导出历史记录请求:", exportData);

            const response = await uldService.exportUldHistory(exportData);

            if (response.data.success) {
                message.success(`${response.data.message}，正在为您下载文件...`);
                await handleExportDownload(response.data.download_url, response.data.file_name);
            } else {
                message.error(response.data.message || "导出失败");
            }
        } catch (error) {
            console.error("导出历史记录失败:", error);
            message.error(error.response?.data?.detail || "导出失败，请稍后重试");
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <div style={{ padding: "24px" }}>
            {/* 页面标题 */}
            <Space align="center" style={{ marginBottom: "24px" }}>
                <HistoryOutlined style={{ fontSize: "24px", color: "#1890ff" }} />
                <Title level={2} style={{ margin: 0 }}>
                    ULD历史记录
                </Title>
            </Space>

            {/* 功能说明 */}
            <Alert message="功能说明" description="此页面展示ULD的完整变更历史，包括创建、更新、位置变更、状态变更等所有操作记录。您可以通过筛选条件查看特定时间段或特定类型的历史记录。" type="info" showIcon style={{ marginBottom: "24px" }} />

            {/* 筛选器 */}
            <Card style={{ marginBottom: "16px" }}>
                <UldHistoryFilter onFilter={handleFilter} />
            </Card>

            {/* 历史记录表格 */}
            <Card
                title="历史记录列表"
                extra={
                    <Space>
                        <span>共 {pagination.total} 条记录</span>
                        <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport} loading={exportLoading} disabled={pagination.total === 0} size="small">
                            导出Excel
                        </Button>
                    </Space>
                }
            >
                <UldHistoryTable data={historyData} loading={loading} pagination={pagination} onPageChange={handlePageChange} onShowSizeChange={handleShowSizeChange} />
            </Card>
        </div>
    );
};

export default UldHistoryPage;
